import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Helmet } from 'react-helmet-async';

import {
  CardContent,
  Grid,
  Link,
  Breadcrumbs,
  Card,
  CircularProgress,
  Divider,
  LinearProgress,
  Paper,
  Typography,
} from '@mui/material';

function CircularIndeterminate() {
  return (
    <Card sx={{ mb: 6 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Circular Indeterminate
        </Typography>
        <Paper sx={{ mt: 3 }}>
          <CircularProgress sx={{ m: 2 }} />
          <CircularProgress sx={{ m: 2 }} color="secondary" />
        </Paper>
      </CardContent>
    </Card>
  );
}

function CircularDeterminate() {
  const [progress, setProgress] = React.useState(0);

  React.useEffect(() => {
    const timer = setInterval(() => {
      setProgress((prevProgress) => (prevProgress >= 100 ? 0 : prevProgress + 10));
    }, 800);

    return () => {
      clearInterval(timer);
    };
  }, []);

  return (
    <Card sx={{ mb: 6 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Circular Determinate
        </Typography>
        <Paper sx={{ mt: 3 }}>
          <CircularProgress variant="determinate" value={progress} />
        </Paper>
      </CardContent>
    </Card>
  );
}

function CircularStatic() {
  return (
    <Card sx={{ mb: 6 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Circular Static
        </Typography>
        <Paper sx={{ mt: 3 }}>
          <CircularProgress sx={{ m: 2 }} variant="determinate" value={5} />
          <CircularProgress sx={{ m: 2 }} variant="determinate" value={25} />
          <CircularProgress sx={{ m: 2 }} variant="determinate" value={50} />
          <CircularProgress sx={{ m: 2 }} variant="determinate" value={75} />
          <CircularProgress sx={{ m: 2 }} variant="determinate" value={100} />
        </Paper>
      </CardContent>
    </Card>
  );
}

function LinearIndeterminate() {
  return (
    <Card sx={{ mb: 6 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Linear Indeterminate
        </Typography>
        <Paper sx={{ mt: 3 }}>
          <LinearProgress sx={{ my: 2 }} />
          <LinearProgress sx={{ my: 2 }} color="secondary" />
        </Paper>
      </CardContent>
    </Card>
  );
}

function LinearDeterminate() {
  const [progress, setProgress] = React.useState(0);

  React.useEffect(() => {
    const timer = setInterval(() => {
      setProgress((oldProgress) => {
        if (oldProgress === 100) {
          return 0;
        }
        const diff = Math.random() * 10;
        return Math.min(oldProgress + diff, 100);
      });
    }, 500);

    return () => {
      clearInterval(timer);
    };
  }, []);

  return (
    <Card sx={{ mb: 6 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Linear Determinate
        </Typography>
        <Paper sx={{ mt: 3 }}>
          <LinearProgress sx={{ my: 2 }} variant="determinate" value={progress} />
          <LinearProgress sx={{ my: 2 }} variant="determinate" value={progress} color="secondary" />
        </Paper>
      </CardContent>
    </Card>
  );
}

export function LinearBuffer({
  loaderCardContentClassName,
}: {
  loaderCardContentClassName?: string;
}) {
  const [progress, setProgress] = React.useState(0);
  const [buffer, setBuffer] = React.useState(10);

  const progressRef = React.useRef(() => {});
  React.useEffect(() => {
    progressRef.current = () => {
      if (progress > 100) {
        setProgress(0);
        setBuffer(10);
      } else {
        const diff = Math.random() * 10;
        const diff2 = Math.random() * 10;
        setProgress(progress + diff);
        setBuffer(progress + diff + diff2);
      }
    };
  });

  React.useEffect(() => {
    const timer = setInterval(() => {
      progressRef.current();
    }, 500);

    return () => {
      clearInterval(timer);
    };
  }, []);

  return (
    <Card elevation={0} sx={{ width: '100%' }}>
      <CardContent
        className={`linear-progress-card-content ${loaderCardContentClassName || ''}`}
        sx={{
          '&.no-padding': {
            padding: 0,
          },
        }}
      >
        <LinearProgress sx={{ my: 2 }} variant="buffer" value={progress} valueBuffer={buffer} />
        <LinearProgress
          sx={{ my: 2 }}
          color="secondary"
          variant="buffer"
          value={progress}
          valueBuffer={buffer}
        />
      </CardContent>
    </Card>
  );
}

export function LinearQuery() {
  return (
    <Card sx={{ mb: 6 }}>
      <CardContent>
        {/* <Typography variant="h6" gutterBottom>
          Linear Query
        </Typography> */}
        <Paper sx={{ mt: 3 }}>
          <LinearProgress sx={{ my: 2 }} variant="query" />
        </Paper>
      </CardContent>
    </Card>
  );
}

function Progress() {
  const navigate = useNavigate();

  return (
    <React.Fragment>
      <Helmet title="Progress" />
      <Typography variant="h3" gutterBottom display="inline">
        Progress
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" sx={{ mt: 2 }}>
        <Link onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
          Dashboard
        </Link>
        <Link onClick={() => navigate('/')} sx={{ cursor: 'pointer' }}>
          Components
        </Link>
        <Typography>Progress</Typography>
      </Breadcrumbs>

      <Divider sx={{ my: 6 }} />

      <Grid container spacing={6}>
        <Grid size={{ xs: 12, md: 6 }}>
          <CircularIndeterminate />
          <CircularDeterminate />
          <CircularStatic />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <LinearIndeterminate />
          <LinearDeterminate />
          <LinearBuffer />
          <LinearQuery />
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default Progress;
